import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import MainApp from "./pages/MainApp";
import SharedStudyPage from "./pages/SharedStudyPage";
import PricingPage from "./pages/PricingPage";
import WelcomePage from "./pages/WelcomePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import SupportPage from "./pages/SupportPage";
import ResourcesPage from "./pages/ResourcesPage";
import GuidePage from "./pages/GuidePage";
import QuizJoinPage from "./pages/QuizJoinPage";
import QuizPresenterPage from "./pages/QuizPresenterPage";
import PresentViewPage from "./pages/PresentViewPage";
import PresenterOutput from "./pages/PresenterOutput";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B3A6B] animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function TrialSetupRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B3A6B] animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If already completed trial checkout, go to app
  if (user.trial_checkout_completed || user.role === 'platform_admin' || user.role === 'admin') {
    return <Navigate to="/app" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<WelcomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/share/:shareId" element={<SharedStudyPage />} />
      <Route path="/quiz/join" element={<QuizJoinPage />} />
      <Route path="/quiz/join/:code" element={<QuizJoinPage />} />
      <Route path="/quiz/present/:quizId" element={<QuizPresenterPage />} />
      <Route path="/present/:code" element={<PresentViewPage />} />
      <Route path="/presenter-output" element={<PresenterOutput />} />
      <Route path="/pricing" element={
        <ProtectedRoute>
          <PricingPage />
        </ProtectedRoute>
      } />
      <Route path="/trial-setup" element={
        <TrialSetupRoute>
          <PricingPage isTrialSetup={true} />
        </TrialSetupRoute>
      } />
      <Route path="/app" element={
        <ProtectedRoute>
          <MainApp />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
